import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { handleUnauthorized } from './authEvents';
import { API_BASE } from './base';
import { refreshAccessToken } from './auth';
import { reportGetFailure, reportGetOk, reportRequestFailure, reportRequestOk } from '@/lib/serverHealth';


export const axiosInstance = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: 30000,
});

axiosInstance.interceptors.request.use(config => {
  try {
    const token = typeof window !== 'undefined' ? localStorage.getItem('jwt') : null;
    if (token) {
      if (!config.headers) config.headers = {} as any;
      (config.headers as any)['Authorization'] = `Bearer ${token}`;
    }
  } catch {
    // noop — non-browser env
  }
  return config;
});

// Single in-flight refresh shared across concurrent 401s so we hit the refresh
// endpoint at most once per access-token expiry.
let refreshPromise: Promise<string> | null = null;

const runRefresh = (): Promise<string> => {
  if (refreshPromise) return refreshPromise;
  const refresh = typeof window !== 'undefined' ? localStorage.getItem('refresh') : null;
  if (!refresh) return Promise.reject(new Error('no refresh token'));
  refreshPromise = refreshAccessToken(refresh)
    .then(resp => {
      const access = resp.data.access;
      try { localStorage.setItem('jwt', access); } catch {}
      return access;
    })
    .finally(() => { refreshPromise = null; });
  return refreshPromise;
};

axiosInstance.interceptors.response.use(
  resp => {
    reportRequestOk();
    if ((resp.config?.method ?? 'get').toLowerCase() === 'get') reportGetOk(resp.config?.url);
    return resp;
  },
  async (error: AxiosError) => {
    const status = error?.response?.status;
    // Без ответа / 502–504 = возможно, бэкенд редеплоится: экраны ошибок покажут «сервер
    // обновляется» и сами перезапросят данные, когда он ответит (Алексей 26.10 #2635).
    // Отменённые запросы (SWR/AbortController) — не сбой связи.
    if (!axios.isCancel(error)) reportRequestFailure(status);
    const original = error?.config as (AxiosRequestConfig & { _retried?: boolean }) | undefined;
    // Сбой GET — в общий реестр: одна плашка на странице вместо текста в каждой секции (#3224).
    if (!axios.isCancel(error) && (original?.method ?? 'get').toLowerCase() === 'get') {
      reportGetFailure(original?.url, status);
    }

    // Don't refresh-and-retry or hard-logout on 401 from credential-check
    // endpoints — those mean "wrong creds", not "session expired", and the
    // login form needs the rejection to display the error.
    const url = original?.url ?? '';
    if (status === 401 && /\/core\/api\/auth\/(jwt\/create|tg-login|tg-exchange)\//.test(url)) {
      return Promise.reject(error);
    }

    if (status === 401 && original && !original._retried) {
      original._retried = true;
      try {
        const access = await runRefresh();
        if (!original.headers) original.headers = {} as any;
        (original.headers as any)['Authorization'] = `Bearer ${access}`;
        return axiosInstance.request(original);
      } catch {
        handleUnauthorized();
        // REJECT (don't hang): the old `new Promise(() => {})` left every
        // in-flight SWR fetcher pending forever — if the refresh call failed
        // on a transient blip (API restarting mid-deploy), the whole app
        // latched: keys stuck "validating", no retry, no refreshInterval
        // re-fire, until a manual reload. The /login navigation from
        // handleUnauthorized proceeds either way; a rejected fetch on a page
        // being navigated away is invisible.
        return Promise.reject(error);
      }
    }

    if (status === 401) {
      handleUnauthorized();
      return Promise.reject(error);
    }
    return Promise.reject(error);
  }
);
